import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { languagesAPI, sessionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Globe, Bot, Headphones, ArrowRight, ArrowLeft, CheckCircle, Loader2, Zap, Sparkles } from 'lucide-react';

const STEPS = [
  { id: 'language', title: 'Choose a Language', subtitle: 'Select the language you need interpreted' },
  { id: 'type', title: 'Session Type', subtitle: 'Choose AI-powered or human interpreter' },
  { id: 'start', title: 'Start Your Session', subtitle: 'Everything is set — launch your first session' },
];

export default function OnboardingWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('es');
  const [sessionType, setSessionType] = useState('ai');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    languagesAPI.getAll().then(res => {
      const all = [...(res.data.sla || []), ...(res.data.supported || [])];
      setLanguages(all.length > 0 ? all : [
        { code: 'es', name: 'Spanish' }, { code: 'zh', name: 'Mandarin' },
        { code: 'fr', name: 'French' }, { code: 'ar', name: 'Arabic' },
        { code: 'vi', name: 'Vietnamese' }, { code: 'ko', name: 'Korean' },
        { code: 'ru', name: 'Russian' }, { code: 'pt', name: 'Portuguese' },
      ]);
    }).catch(() => {
      setLanguages([
        { code: 'es', name: 'Spanish' }, { code: 'zh', name: 'Mandarin' },
        { code: 'fr', name: 'French' }, { code: 'ar', name: 'Arabic' },
      ]);
    });
  }, []);

  const handleCreateSession = async () => {
    setCreating(true);
    try {
      const res = await sessionsAPI.create({ target_language: selectedLang, session_type: sessionType, domain_level: 'BALANCED' });
      // Mark onboarding as complete
      localStorage.setItem(`onboarding_complete_${user.id}`, 'true');
      navigate(`/client/session/${res.data.id}`);
    } catch (err) {
      console.error(err);
      setCreating(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem(`onboarding_complete_${user.id}`, 'true');
    navigate('/client');
  };

  const langName = languages.find(l => l.code === selectedLang)?.name || selectedLang;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex flex-col" data-testid="onboarding-wizard">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-white/10">
        <img src="/logo-white.png" alt="Interpreter IO" className="h-8" />
        <Button variant="ghost" className="text-slate-400 hover:text-white text-sm" onClick={handleSkip} data-testid="skip-onboarding">
          Skip setup
        </Button>
      </div>

      {/* Progress */}
      <div className="px-8 pt-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${
                  i < step ? 'bg-green-500 text-white' : i === step ? 'bg-blue-600 text-white' : 'bg-white/10 text-slate-500'
                }`}>
                  {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`h-px flex-1 transition-colors ${i < step ? 'bg-green-500' : 'bg-white/10'}`} />}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-1">Step {step + 1} of {STEPS.length}</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-8 pb-16">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: 'DM Sans' }}>
              {STEPS[step].title}
            </h1>
            <p className="text-slate-400 text-base">{STEPS[step].subtitle}</p>
          </div>

          {/* Step 1: Language Selection */}
          {step === 0 && (
            <div className="space-y-6" data-testid="step-language">
              <div className="bg-white/5 border border-white/10 rounded-xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Globe className="w-6 h-6 text-blue-400" />
                  <span className="text-lg font-medium">Target Language</span>
                </div>
                <Select value={selectedLang} onValueChange={setSelectedLang}>
                  <SelectTrigger className="w-full bg-white/5 border-white/20 text-white h-12 text-base" data-testid="onboarding-language-select">
                    <SelectValue placeholder="Select a language" />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map(l => (
                      <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-slate-500 mt-4">
                  You'll be speaking English and the AI will interpret to and from <span className="text-blue-400 font-medium">{langName}</span>.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {['Spanish', 'Mandarin', 'French'].map(lang => {
                  const code = lang === 'Spanish' ? 'es' : lang === 'Mandarin' ? 'zh' : 'fr';
                  return (
                    <button key={code} onClick={() => setSelectedLang(code)}
                      className={`py-3 px-4 rounded-lg border text-sm font-medium transition-all ${selectedLang === code ? 'border-blue-500 bg-blue-600/20 text-blue-300' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'}`}
                      data-testid={`quick-lang-${code}`}>
                      {lang}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Session Type */}
          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="step-type">
              <button
                onClick={() => setSessionType('ai')}
                className={`rounded-xl border p-6 text-left transition-all ${sessionType === 'ai' ? 'border-blue-500 bg-blue-600/10 ring-1 ring-blue-500/50' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
                data-testid="type-ai"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sessionType === 'ai' ? 'bg-blue-600' : 'bg-white/10'}`}>
                    <Bot className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold">AI Interpreter</p>
                    <p className="text-xs text-slate-400">Instant • 250+ languages</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-blue-400" />Starts in under 2 seconds</li>
                  <li className="flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-blue-400" />Lower cost per minute</li>
                  <li className="flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-blue-400" />Great for routine conversations</li>
                </ul>
              </button>
              <button
                onClick={() => setSessionType('human')}
                className={`rounded-xl border p-6 text-left transition-all ${sessionType === 'human' ? 'border-emerald-500 bg-emerald-600/10 ring-1 ring-emerald-500/50' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
                data-testid="type-human"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sessionType === 'human' ? 'bg-emerald-600' : 'bg-white/10'}`}>
                    <Headphones className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold">Human Interpreter</p>
                    <p className="text-xs text-slate-400">Premium • SLA-backed</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-emerald-400" />Professional human interpreters</li>
                  <li className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-emerald-400" />Best for high-stakes situations</li>
                  <li className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-emerald-400" />Legal, medical, compliance</li>
                </ul>
              </button>
            </div>
          )}

          {/* Step 3: Launch */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-start">
              <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600/20 mb-6">
                  {sessionType === 'ai' ? <Bot className="w-8 h-8 text-blue-400" /> : <Headphones className="w-8 h-8 text-emerald-400" />}
                </div>
                <h3 className="text-xl font-bold mb-2">Ready to go!</h3>
                <p className="text-slate-400 mb-6">
                  You're about to start a {sessionType === 'ai' ? 'AI-powered' : 'human'} interpreting session in <span className="text-blue-400 font-semibold">{langName}</span>.
                </p>
                <div className="flex flex-wrap justify-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1 bg-white/5 px-3 py-1.5 rounded-full">
                    <Globe className="w-3 h-3" /> {langName}
                  </span>
                  <span className="flex items-center gap-1 bg-white/5 px-3 py-1.5 rounded-full">
                    {sessionType === 'ai' ? <Bot className="w-3 h-3" /> : <Headphones className="w-3 h-3" />}
                    {sessionType === 'ai' ? 'AI Interpreter' : 'Human Interpreter'}
                  </span>
                </div>
              </div>

              <Button
                onClick={handleCreateSession}
                disabled={creating}
                className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold rounded-xl"
                data-testid="launch-session-button"
              >
                {creating ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ArrowRight className="w-5 h-5 mr-2" />}
                {creating ? 'Starting session...' : 'Launch Session'}
              </Button>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button
              variant="ghost"
              className="text-slate-400 hover:text-white"
              onClick={() => setStep(s => s - 1)}
              disabled={step === 0}
              data-testid="wizard-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 && (
              <Button
                onClick={() => setStep(s => s + 1)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="wizard-next"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
